import { i18n } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import { Trans, useLingui } from '@lingui/react/macro';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { type FormEvent, useEffect, useRef, useState } from 'react';
import { CenteredShell, FieldLabel, PageEyebrow } from '../components/auth-shells.js';
import { Mag } from '../components/mag.js';
import { magToast } from '../components/toaster.js';
import { updateWorkspaceNameApi } from '../lib/api.js';
import { founderErrorCopy } from '../lib/error-copy.js';
import { billingQuery, brandsQuery, meQuery } from '../lib/queries.js';

// [12.2L-51] new-account welcome screen. inserted between signup and
// /onboarding/plan so the founder's first frame in the product is mag
// saying hi + a single input to name the workspace. without this the
// user landed on the plan picker with a workspace called
// "{firstName}'s workspace" they had no chance to change.

export const Route = createFileRoute('/onboarding/welcome')({
  beforeLoad: async ({ context: { queryClient } }) => {
    const me = await queryClient.ensureQueryData(meQuery());
    if (!me) throw redirect({ to: '/login' });
    // [12.2L-55] Skip past welcome when the founder is clearly not a
    // first-touch user anymore — either they have a live brand, or
    // they've already picked a plan. Otherwise the screen would
    // re-fire every time they backtrack to /home, which would loop
    // them through "hi. i'm mag." after they already met her.
    //
    // Value-first reorder: bounce to /home (the central router) rather
    // than straight to /onboarding/plan — the paywall now lives at the
    // END of brand onboarding, so /home resolves the right resume point
    // (create-brand, resume onboarding, or paywall) instead of pushing
    // a not-yet-onboarded founder at payment.
    const [brands, billing] = await Promise.all([
      queryClient.ensureQueryData(brandsQuery()),
      queryClient.ensureQueryData(billingQuery()),
    ]);
    const liveBrands = brands.filter((b) => b.archivedAt === null);
    if (liveBrands.length > 0 || billing.subscriptionStatus !== 'none') {
      throw redirect({ to: '/home' });
    }
    return { me };
  },
  loader: ({ context }) => context,
  component: WelcomePage,
});

function WelcomePage() {
  const { t } = useLingui();

  const { me } = Route.useLoaderData();
  const navigate = useNavigate();
  const defaultName = me?.workspaceName ?? '';
  const [name, setName] = useState(defaultName);
  const [pending, setPending] = useState(false);

  // Auto-focus the workspace name input after the stagger reveal
  // settles — same pattern as /onboarding/url so the founder can
  // type immediately without clicking the field.
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    const timer = window.setTimeout(() => inputRef.current?.focus(), 600);
    return () => window.clearTimeout(timer);
  }, []);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) {
      magToast.warning(
        i18n._(msg`name the workspace first.`),
        i18n._(msg`i suggest something short.`),
      );
      return;
    }
    setPending(true);
    try {
      // Only call the api if the name actually changed — otherwise we
      // just navigate on. PATCH /api/workspace is idempotent but the
      // extra round-trip is wasted on the common "Luis's workspace is
      // fine" path.
      if (trimmed !== defaultName) {
        await updateWorkspaceNameApi(trimmed);
      }
      // Value-first reorder: go create the first brand next (was
      // /onboarding/plan). Payment now comes after the brand's
      // onboarding shows its worth.
      void navigate({ to: '/brands' });
    } catch (err) {
      const message = founderErrorCopy(err, 'try again.');
      magToast.error("couldn't save the workspace name.", message);
      setPending(false);
    }
  };

  return (
    <CenteredShell>
      <div className="mag-stagger-in relative" style={{ animationDelay: '80ms' }}>
        <div
          aria-hidden="true"
          className="mag-aura pointer-events-none absolute inset-[-18px] rounded-full"
        />
        <div className="mag-breathe relative">
          <Mag size={86} mood="default" />
        </div>
      </div>
      <div className="mag-stagger-in" style={{ animationDelay: '160ms' }}>
        <PageEyebrow>
          <Trans>welcome</Trans>
        </PageEyebrow>
      </div>
      <h1
        className="mag-stagger-in font-display text-[40px] sm:text-[56px] font-black leading-[1] tracking-[-0.04em]"
        style={{ animationDelay: '240ms' }}
      >
        <Trans>hi. i'm mag.</Trans>
      </h1>
      <p
        className="mag-stagger-in font-serif text-xl leading-tight text-[var(--ink)]/80"
        style={{ animationDelay: '320ms' }}
      >
        <Trans>
          “i run ads, write your monday memo, and ping you only when it matters. before we start —
          what should i call your workspace?”
        </Trans>
      </p>

      <form
        onSubmit={handleSubmit}
        className="mag-stagger-in mt-2 flex w-full max-w-[460px] flex-col items-stretch gap-3"
        style={{ animationDelay: '400ms' }}
      >
        <label className="field">
          <FieldLabel>
            <Trans>workspace name</Trans>
          </FieldLabel>
          <input
            ref={inputRef}
            type="text"
            autoComplete="organization"
            required
            maxLength={80}
            placeholder={t`acme co.`}
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={pending}
          />
        </label>
        <div className="mt-1 flex items-center justify-end gap-3">
          <button type="submit" disabled={pending} className="btn disabled:opacity-60">
            {pending ? <Trans>saving…</Trans> : <Trans>continue</Trans>}
            <span className="btn-arrow">→</span>
          </button>
        </div>
      </form>

      <p
        className="mag-stagger-in mt-3 mag-voice text-[13.5px] leading-[1.5] text-[var(--ink)]/55"
        style={{ animationDelay: '480ms' }}
      >
        <Trans>
          you can rename this anytime in settings. it's just how i'll refer to your account.
        </Trans>
      </p>
    </CenteredShell>
  );
}
